import React, { useState, useRef } from "react";
import axios from "axios";
import { resizeImageForUpload } from "@/lib/imageResize";
import TmdbLookupModal from "@/components/TmdbLookupModal";
import TmdbChannelArtworkModal from "@/components/TmdbChannelArtworkModal";

export default function ChannelManagementPanel({
  channels,
  categories,
  selectedChannel,
  setSelectedChannel,
  apiBase,
  authHeaders,
  busy,
  setBusy,
  append,
  onChannelsChanged,
  channelVideoCount = 0,
  videosByChannel = {},
}) {
  const [renameValue, setRenameValue] = useState("");
  const [descValue, setDescValue] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  // Sub-channel "RENAME SHOW" flow — inline input that bulk-PATCHes
  // `show_title` on every tape in the sub-channel to a new value.
  // Sub-channels are derived from `show_title`, so renaming the sub
  // = updating every underlying tape's tag. Also migrates the
  // parent's `show_art[oldName]` entry to `show_art[newName]` so any
  // curated per-show artwork survives the rename.
  const [showRenaming, setShowRenaming] = useState(false);
  const [showRenameValue, setShowRenameValue] = useState("");
  // Seasonal dormant toggle — flip once to preload a Christmas /
  // Halloween / birthday rail without exposing it to viewers, flip
  // again to broadcast. Simple boolean, no dates. When true the
  // /api/channels list hides the channel from non-owner requests so
  // it drops out of the guide, EPG, ch-up/down cycling and preview
  // grid instantly.
  const [dormantBusy, setDormantBusy] = useState(false);
  const [thumbBust, setThumbBust] = useState(Date.now());
  // Which asset slot the TMDb picker is targeting ("thumbnail" |
  // "guide_poster"). `null` = closed. Both slots share one modal.
  const [tmdbTargetKind, setTmdbTargetKind] = useState(null);
  // PHASE 2.3.1 — UNIFIED TMDB ARTWORK UI
  const [tmdbArtworkOpen, setTmdbArtworkOpen] = useState(false);
  // Channel artwork URL drafts are edited together and committed with one
  // SAVE ARTWORK action. This removes the old per-row LINK URL → SAVE
  // workflow while preserving file upload and TMDb as alternate sources.
  const [artworkDrafts, setArtworkDrafts] = useState({
    thumbnail: "",
    logo: "",
    guide_poster: "",
  });
  // Jump-to-number input — lets the owner type a target channel position
  // (1..N) and press GO/Enter instead of clicking ▲/▼ N times. Kept as a
  // string so half-typed input doesn't kick the field into NaN territory.
  const [jumpTarget, setJumpTarget] = useState("");
  const thumbInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const guidePosterInputRef = useRef(null);

  const currentCh = channels.find((c) => c.id === selectedChannel);
  // Sub-channels (Sky-Q "05.1 SONIC UNDERGROUND" derived rows) share
  // one parent DB doc. All channel-level API calls (rename, patch,
  // upload asset) route to the parent's real id instead — otherwise
  // the backend 404s on the synthetic sub id. Sub selection also
  // hides destructive/positional controls (delete, reorder, jump)
  // since those affect the whole parent lineup, not the sub.
  const isSubChannel = !!currentCh?.isSubChannel;
  const editChannelId = isSubChannel ? currentCh.parent_id : selectedChannel;
  const parentCh = isSubChannel
    ? channels.find((c) => c.id === currentCh.parent_id) || null
    : null;

  const currentArtworkUrls = React.useMemo(() => ({
    thumbnail: isSubChannel
      ? ((parentCh?.show_art && parentCh.show_art[currentCh?.show_title]) || "")
      : (currentCh?.thumbnail_url || ""),
    logo: isSubChannel ? "" : (currentCh?.logo_url || ""),
    guide_poster: isSubChannel ? "" : (currentCh?.guide_poster_url || ""),
  }), [
    isSubChannel,
    parentCh?.show_art,
    currentCh?.show_title,
    currentCh?.thumbnail_url,
    currentCh?.logo_url,
    currentCh?.guide_poster_url,
  ]);

  React.useEffect(() => {
    setArtworkDrafts(currentArtworkUrls);
  }, [selectedChannel, currentArtworkUrls]);

  const artworkDirty = ["thumbnail", ...(isSubChannel ? [] : ["logo", "guide_poster"])].some(
    (kind) => (artworkDrafts[kind] || "").trim() !== (currentArtworkUrls[kind] || "").trim()
  );

  const setArtworkDraft = (kind, value) => {
    setArtworkDrafts((prev) => ({ ...prev, [kind]: value }));
  };
  // Reorder-only view of the channel list — sub-channels aren't
  // sortable positions on their own, so ▲/▼ + JUMP TO CH indices must
  // be computed against the real-channel-only sub-list.
  const positionChannels = channels.filter((c) => !c.isSubChannel);
  const positionIdx = positionChannels.findIndex((c) => c.id === editChannelId);
  const idx = positionIdx;

  const handleReorder = async (dir) => {
    if (!selectedChannel || isSubChannel) return;
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= positionChannels.length) return;
    const next = [...positionChannels];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setBusy(true);
    append("> REORDERING CHANNELS...");
    try {
      await axios.post(
        `${apiBase}/channels/reorder`,
        { channel_ids: next.map((c) => c.id) },
        { headers: authHeaders() }
      );
      append("> CHANNEL LINEUP UPDATED.");
      await onChannelsChanged();
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Jump the currently-selected channel to an arbitrary position in the
  // lineup (1..N). Builds the same `channel_ids` array as
  // handleReorder, but splices the selection in at the target index in
  // one shot — no need to step through ▲/▼ N times. Sub-channels are
  // read-only positional (their number is derived from the parent), so
  // this bails out for subs.
  const handleJumpTo = async () => {
    if (!selectedChannel || isSubChannel) return;
    const target = parseInt(jumpTarget, 10);
    if (Number.isNaN(target) || target < 1 || target > positionChannels.length) {
      append(`> ERROR: TARGET MUST BE 1-${positionChannels.length}`);
      return;
    }
    if (idx < 0) return;
    const targetIdx = target - 1;
    if (targetIdx === idx) {
      setJumpTarget("");
      return;
    }
    const next = positionChannels.slice();
    const [moved] = next.splice(idx, 1);
    next.splice(targetIdx, 0, moved);
    setBusy(true);
    append(`> MOVING CH ${currentCh?.number} → CH ${target}...`);
    try {
      await axios.post(
        `${apiBase}/channels/reorder`,
        { channel_ids: next.map((c) => c.id) },
        { headers: authHeaders() }
      );
      append("> CHANNEL LINEUP UPDATED.");
      setJumpTarget("");
      await onChannelsChanged();
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleRename = async () => {
    if (!currentCh || isSubChannel) return;
    const nextName = renameValue.trim();
    const nextDesc = descValue;
    const nameChanged = nextName && nextName !== currentCh.name;
    const descChanged = nextDesc !== (currentCh.description || "");
    if (!nameChanged && !descChanged) {
      setRenaming(false);
      return;
    }
    setBusy(true);
    append(`> SAVING CH ${currentCh.number} ...`);
    try {
      const body = {};
      if (nameChanged) body.name = nextName;
      if (descChanged) body.description = nextDesc;
      await axios.patch(
        `${apiBase}/channels/${editChannelId}`,
        body,
        { headers: authHeaders() }
      );
      append("> SAVED.");
      setRenaming(false);
      setRenameValue("");
      setDescValue("");
      await onChannelsChanged();
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const startRename = () => {
    if (!currentCh) return;
    setRenameValue(currentCh.name);
    setDescValue(currentCh.description || "");
    setRenaming(true);
    setDeleteConfirm(false);
  };

  // Toggle seasonal / dormant mode. When dormant → live, App.js's
  // fetchChannels watcher will pick up the transition and pop a
  // toast globally; here we just append the terminal log so the
  // operator has a receipt in the VCR console.
  const handleToggleDormant = async () => {
    if (!currentCh || isSubChannel) return;
    const next = !currentCh.is_dormant;
    setDormantBusy(true);
    append(
      `> ${next ? "GOING DORMANT" : "GOING LIVE"} — CH ${String(currentCh.number).padStart(2, "0")} · ${currentCh.name}...`
    );
    try {
      await axios.patch(
        `${apiBase}/channels/${editChannelId}`,
        { is_dormant: next },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );
      append(
        next
          ? `> CH ${String(currentCh.number).padStart(2, "0")} IS NOW DORMANT — hidden from viewers.`
          : `> CH ${String(currentCh.number).padStart(2, "0")} IS NOW LIVE — visible to all viewers.`
      );
      await onChannelsChanged();
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setDormantBusy(false);
    }
  };

  // Sub-channel rename — bulk-updates `show_title` on every tape that
  // currently belongs to this sub, then migrates the parent's per-show
  // art entry to the new key so curated artwork survives. Auto-selects
  // the new sub-channel id after refresh (the id includes the show
  // slug, which changes with the name).
  const startShowRename = () => {
    if (!currentCh || !isSubChannel) return;
    setShowRenameValue(currentCh.show_title || currentCh.name || "");
    setShowRenaming(true);
    setDeleteConfirm(false);
  };

  const cancelShowRename = () => {
    setShowRenaming(false);
    setShowRenameValue("");
  };

  const handleRenameShow = async () => {
    if (!currentCh || !isSubChannel || !parentCh) return;
    const oldTitle = (currentCh.show_title || "").trim();
    const nextTitle = (showRenameValue || "").trim().slice(0, 120);
    if (!nextTitle) {
      append("> ERROR: NEW SHOW NAME REQUIRED.");
      return;
    }
    if (nextTitle === oldTitle) {
      cancelShowRename();
      return;
    }
    // Tapes that belong to this sub — filtered from the parent's
    // full video list rather than the sub's own bucket so we always
    // work off the freshest data even if the caller's map is stale.
    const parentTapes = videosByChannel[parentCh.id] || [];
    const tapes = parentTapes.filter(
      (v) => (v.show_title || "").trim() === oldTitle
    );
    if (!tapes.length) {
      append(`> NO TAPES FOUND TAGGED "${oldTitle}" — NOTHING TO RENAME.`);
      cancelShowRename();
      return;
    }
    setBusy(true);
    append(
      `> RENAMING SHOW "${oldTitle}" → "${nextTitle}" — ${tapes.length} TAPE${tapes.length === 1 ? "" : "S"}...`
    );
    let ok = 0;
    let fail = 0;
    const results = await Promise.allSettled(
      tapes.map((v) =>
        axios.patch(
          `${apiBase}/videos/${v.id}`,
          { show_title: nextTitle },
          { headers: { "Content-Type": "application/json", ...authHeaders() } }
        )
      )
    );
    for (const r of results) (r.status === "fulfilled" ? ok++ : fail++);

    // Migrate curated per-show art (if any) to the new key so the
    // sub-channel's thumbnail doesn't reset to the auto-cascade.
    const curatedArt =
      parentCh.show_art && typeof parentCh.show_art === "object"
        ? parentCh.show_art[oldTitle]
        : null;
    if (curatedArt) {
      try {
        // Point the old key at null (clear) and the new key at the
        // curated URL — two calls because the endpoint takes a single
        // show_title per request.
        await axios.post(
          `${apiBase}/channels/${parentCh.id}/show-art/link`,
          { show_title: nextTitle, url: curatedArt },
          { headers: { "Content-Type": "application/json", ...authHeaders() } }
        );
        await axios.post(
          `${apiBase}/channels/${parentCh.id}/show-art/link`,
          { show_title: oldTitle, url: null },
          { headers: { "Content-Type": "application/json", ...authHeaders() } }
        );
      } catch (e) {
        append(`> WARN: SHOW-ART MIGRATION FAILED — ${e.response?.data?.detail || e.message}`);
      }
    }

    append(`> DONE. ${ok} RENAMED${fail ? `, ${fail} FAILED` : ""}.`);
    // Compute the new sub id so we can keep selection on the renamed
    // sub after the refresh (slug derives from the show title).
    const slug = nextTitle
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-+)|(-+$)/g, "")
      .slice(0, 60);
    const newSubId = `${parentCh.id}::sub::${slug || `show-${currentCh.sub_index || 1}`}`;
    cancelShowRename();
    await onChannelsChanged();
    // Best-effort re-selection — if the new id doesn't exist (edge
    // case: only 1 distinct show remains after rename → sub-channels
    // collapse), fall back to the parent.
    setTimeout(() => {
      setSelectedChannel(newSubId);
    }, 0);
    setBusy(false);
  };

  const handleDelete = async () => {
    if (!currentCh || isSubChannel) return;
    setBusy(true);
    // The backend cascades — DELETE /channels/{id} also soft-deletes every
    // tape that belongs to it. Surface the cascade in the terminal so the
    // owner sees exactly what's about to vanish.
    const tapeNote = channelVideoCount > 0
      ? ` + ${channelVideoCount} TAPE${channelVideoCount === 1 ? "" : "S"}`
      : "";
    append(`> DELETING CH ${currentCh.number}${tapeNote} ...`);
    try {
      await axios.delete(`${apiBase}/channels/${selectedChannel}`, { headers: authHeaders() });
      append(`> CHANNEL${tapeNote.replace(/^ \+ /, " + ")} DESTROYED.`);
      const remaining = channels.filter((c) => c.id !== selectedChannel);
      setSelectedChannel(remaining[0] ? remaining[0].id : null);
      setDeleteConfirm(false);
      await onChannelsChanged();
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const uploadAsset = async (kind, file) => {
    if (!selectedChannel || !file) return;
    // Sub-channels have no DB row so `/channels/{sub}/thumbnail`
    // returns 404. UPLOAD-mode isn't supported on subs yet — only the
    // LINK-URL flow (which routes to the parent's per-show art map)
    // is safe. Surface a clear message rather than a silent 404.
    if (isSubChannel) {
      append(`> ${kind.toUpperCase()} UPLOAD NOT SUPPORTED ON SUB-CHANNELS — use LINK URL to set per-show art, or upload on the parent channel.`);
      return;
    }
    setBusy(true);
    append(`> UPLOADING ${kind.toUpperCase()} FOR CH ${currentCh?.number}...`);
    try {
      // Downscale + re-encode in the browser before shipping — even a
      // 10 MB source photo comes out ~120 KB after this. Bypasses the
      // silent-failure mode where a large multipart body could get
      // rejected somewhere in the network path with no error surfaced.
      //
      // EXCEPTION: guide posters can be animated GIFs. The resize
      // helper draws to a canvas, which flattens GIFs to a single frame
      // — killing the animation. So we ship animated GIFs untouched
      // (`image/gif` mime), but STILL resize any non-GIF guide poster
      // so a 10 MB PNG doesn't blow Cloudflare's origin-response window.
      // This is why the thumbnail/logo uploads always work: they're
      // tiny after the canvas re-encode.
      const isGif = kind === "guide_poster" && (
        (file.type || "").toLowerCase() === "image/gif" ||
        /\.gif$/i.test(file.name || "")
      );
      const shipped = isGif ? file : await resizeImageForUpload(file);
      const form = new FormData();
      form.append("file", shipped);
      await axios.post(`${apiBase}/channels/${editChannelId}/${kind}`, form, {
        headers: { "Content-Type": "multipart/form-data", ...authHeaders() },
      });
      append(`> ${kind.toUpperCase()} UPDATED.`);
      await onChannelsChanged();
      setThumbBust(Date.now());
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const clearAsset = async (kind) => {
    if (!selectedChannel) return;
    // Sub-channels: clear the per-show art entry on the parent
    // instead of hitting the non-existent sub DB row (which 404s).
    // linkAsset already handles the LINK-URL flow via
    // `POST /channels/{parent}/show-art/link { url: null }`; reuse it
    // so both flows converge on one endpoint.
    if (isSubChannel) {
      await linkAsset(kind, "");
      return;
    }
    setBusy(true);
    append(`> CLEARING ${kind.toUpperCase()}...`);
    try {
      await axios.delete(`${apiBase}/channels/${editChannelId}/${kind}`, { headers: authHeaders() });
      append(`> ${kind.toUpperCase()} CLEARED.`);
      await onChannelsChanged();
      setThumbBust(Date.now());
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Radio-toggle alternative to uploadAsset: set an external image URL
  // instead of shipping bytes. Backend nulls the storage path so only
  // one source is active. Send url="" to clear (identical result to
  // clearAsset since the DELETE endpoint nulls both fields).
  const linkAsset = async (kind, url) => {
    if (!selectedChannel) return;
    setBusy(true);
    append(`> LINKING ${kind.toUpperCase()} URL...`);
    try {
      // Sub-channels (Sky-Q "05.1 SONIC UNDERGROUND" style) are derived
      // on the frontend — they have no DB row, so hitting the normal
      // `/channels/{id}/{kind}/link` returns 404. Route thumbnail
      // requests to the parent's per-show art map instead. Logo /
      // guide_poster don't have a per-show equivalent yet, so we
      // surface a clear message rather than a silent 404.
      const chObj = (channels || []).find((c) => c.id === selectedChannel);
      if (chObj && chObj.isSubChannel) {
        if (kind !== "thumbnail") {
          append(`> ${kind.toUpperCase()} URL LINK NOT SUPPORTED ON SUB-CHANNELS — only THUMBNAIL is per-show. Link the parent channel's ${kind} instead.`);
          setBusy(false);
          return;
        }
        await axios.post(
          `${apiBase}/channels/${chObj.parent_id}/show-art/link`,
          { show_title: chObj.show_title, url: url || null },
          { headers: { "Content-Type": "application/json", ...authHeaders() } }
        );
      } else {
        await axios.post(
          `${apiBase}/channels/${selectedChannel}/${kind}/link`,
          { url: url || null },
          { headers: { "Content-Type": "application/json", ...authHeaders() } }
        );
      }
      append(url ? `> ${kind.toUpperCase()} LINKED.` : `> ${kind.toUpperCase()} LINK CLEARED.`);
      await onChannelsChanged();
      setThumbBust(Date.now());
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const saveArtworkUrls = async () => {
    if (!selectedChannel || !artworkDirty) return;

    const kinds = ["thumbnail", ...(isSubChannel ? [] : ["logo", "guide_poster"])];
    const changed = kinds.filter(
      (kind) => (artworkDrafts[kind] || "").trim() !== (currentArtworkUrls[kind] || "").trim()
    );
    if (!changed.length) return;

    setBusy(true);
    append(`> SAVING CHANNEL ARTWORK (${changed.length} CHANGE${changed.length === 1 ? "" : "S"})...`);
    try {
      const chObj = (channels || []).find((c) => c.id === selectedChannel);
      for (const kind of changed) {
        const url = (artworkDrafts[kind] || "").trim();
        if (chObj?.isSubChannel) {
          await axios.post(
            `${apiBase}/channels/${chObj.parent_id}/show-art/link`,
            { show_title: chObj.show_title, url: url || null },
            { headers: { "Content-Type": "application/json", ...authHeaders() } }
          );
        } else {
          await axios.post(
            `${apiBase}/channels/${selectedChannel}/${kind}/link`,
            { url: url || null },
            { headers: { "Content-Type": "application/json", ...authHeaders() } }
          );
        }
      }
      append("> CHANNEL ARTWORK SAVED.");
      await onChannelsChanged();
      setThumbBust(Date.now());
    } catch (e) {
      append(`> ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };


  return (
    <div data-testid="channel-management-panel">
      {/* Phase 2.3.85 — split live and dormant channel access while
          preserving one shared selectedChannel/editor state beneath.
          Live channels retain category grouping; dormant channels get
          their own dedicated picker for quick seasonal/preload access. */}
      {(() => {
        const catList = Array.isArray(categories) ? categories : [];
        const orderedCats = [...catList].sort((a, b) => (a.order || 0) - (b.order || 0));
        // Bucket channels
        const buckets = new Map(orderedCats.map((c) => [c.id, []]));
        const uncategorised = [];
        const virtuals = [];
        // Sub-channels bucketed by parent so the picker groups them
        // adjacent to their parent's category — makes it obvious that
        // "05.1 SONIC UNDERGROUND" belongs under CH 05. Parent-id →
        // list of sub rows. Order-of-insertion follows the natural
        // sub_index order so "05.1" precedes "05.2".
        const subsByParent = new Map();
        // Dormant (seasonal / preload) channels are pulled out of
        // their normal category and shown in a dedicated picker below
        // the live lineup so Christmas, Halloween, birthday and other
        // preloaded rails remain easy to reach without cluttering the
        // everyday live-channel selector.
        const dormantChannels = [];
        for (const ch of channels || []) {
          if (ch.virtual) { virtuals.push(ch); continue; }
          if (ch.isSubChannel) {
            const parentId = ch.parent_id;
            if (!subsByParent.has(parentId)) subsByParent.set(parentId, []);
            subsByParent.get(parentId).push(ch);
            continue;
          }
          if (ch.is_dormant) {
            dormantChannels.push(ch);
            continue;
          }
          if (ch.category_id && buckets.has(ch.category_id)) {
            buckets.get(ch.category_id).push(ch);
          } else {
            uncategorised.push(ch);
          }
        }
        const sortByNumber = (a, b) => (a.number || 0) - (b.number || 0);
        const sortBySubIdx = (a, b) => (a.sub_index || 0) - (b.sub_index || 0);
        // Renderer: parent option immediately followed by its
        // sub-channels (each labelled "05.1 · SONIC UNDERGROUND")
        // so the operator can jump straight into the sub to view its
        // tapes without scanning a separate section.
        const renderChannelWithSubs = (c) => {
          const subs = (subsByParent.get(c.id) || []).sort(sortBySubIdx);
          const dormantMark = c.is_dormant ? "  🌙 DORMANT" : "";
          return [
            <option key={c.id} value={c.id} className="bg-black text-[var(--phosphor)]">
              CH {String(c.number).padStart(2, "0")} — {c.name}{dormantMark}
            </option>,
            ...subs.map((s) => (
              <option key={s.id} value={s.id} className="bg-black text-[color-mix(in_srgb,var(--phosphor)_80%,transparent)]">
                {"\u00A0\u00A0"}└ {s.numberLabel || `${String(c.number).padStart(2, "0")}.${s.sub_index}`} · {s.name}
              </option>
            )),
          ];
        };
        return (
          (() => {
            const selectedParent = currentCh?.isSubChannel
              ? channels.find((c) => c.id === currentCh.parent_id) || null
              : null;
            const selectedIsDormant = !!(
              currentCh?.is_dormant ||
              selectedParent?.is_dormant
            );
            const liveValue = selectedChannel && !selectedIsDormant
              ? selectedChannel
              : "";
            const dormantValue = selectedChannel && selectedIsDormant
              ? selectedChannel
              : "";

            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
                <div>
                  <div className="opacity-80 mb-1">&gt; LIVE CHANNELS:</div>
                  <select
                    value={liveValue}
                    onChange={(e) => {
                      if (e.target.value) setSelectedChannel(e.target.value);
                    }}
                    data-testid="channel-select"
                    className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-2 outline-none"
                  >
                    {!liveValue && (
                      <option value="" disabled className="bg-black text-[var(--phosphor)]">
                        SELECT LIVE CHANNEL
                      </option>
                    )}
            {orderedCats.map((cat) => {
              const list = (buckets.get(cat.id) || []).sort(sortByNumber);
              if (!list.length) return null;
              return (
                <optgroup key={cat.id} label={cat.name} className="bg-black text-[var(--phosphor)]">
                  {list.flatMap(renderChannelWithSubs)}
                </optgroup>
              );
            })}
            {uncategorised.length > 0 && (
              <optgroup label="OTHER (uncategorised)" className="bg-black text-[var(--phosphor)]">
                {uncategorised.sort(sortByNumber).flatMap(renderChannelWithSubs)}
              </optgroup>
            )}
            {virtuals.length > 0 && (
              <optgroup label="SYSTEM (virtual)" className="bg-black text-[var(--phosphor)]">
                {virtuals.map((c) => (
                  <option key={c.id} value={c.id} className="bg-black text-[var(--phosphor)]">
                    ★ {c.name}
                  </option>
                ))}
              </optgroup>
            )}
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <div className="text-[#FFAA33] opacity-90">
                      &gt; DORMANT CHANNELS:
                    </div>
                    <div
                      className="text-[10px] tracking-[0.18em] text-[#FFAA33] opacity-70"
                      data-testid="dormant-channel-count"
                    >
                      {dormantChannels.length}
                    </div>
                  </div>

                  {dormantChannels.length > 0 ? (
                    <select
                      value={dormantValue}
                      onChange={(e) => {
                        if (e.target.value) setSelectedChannel(e.target.value);
                      }}
                      data-testid="dormant-channel-select"
                      className="w-full bg-black border border-[#FFAA33] text-[#FFAA33] p-2 outline-none"
                    >
                      {!dormantValue && (
                        <option value="" disabled className="bg-black text-[#FFAA33]">
                          SELECT DORMANT CHANNEL
                        </option>
                      )}
                      {dormantChannels
                        .sort(sortByNumber)
                        .flatMap(renderChannelWithSubs)}
                    </select>
                  ) : (
                    <div
                      data-testid="no-dormant-channels"
                      className="w-full border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] px-3 py-2 text-[11px] tracking-[0.16em] opacity-45"
                    >
                      NO DORMANT CHANNELS
                    </div>
                  )}
                </div>
              </div>
            );
          })()
        );
      })()}

      {/* Sub-channel banner — makes it explicit that channel-level
          actions on a sub route to the parent's DB row. Keeps the
          operator from wondering why RENAME/DELETE aren't clickable
          when they're viewing a Sky-Q derived sub. Also offers a
          "RENAME SHOW" action that bulk-updates `show_title` on
          every tape in this sub. */}
      {isSubChannel && parentCh && (
        <div
          className="mt-2 border border-[#FFAA33]/60 bg-[color-mix(in_srgb,#FFAA33_10%,black)] px-2 py-1.5 text-[10px] tracking-widest text-[#FFAA33]"
          data-testid="sub-channel-banner"
        >
          <div className="mb-1.5">
            ▲ SUB-CHANNEL — derived from CH {String(parentCh.number).padStart(2, "0")} · {parentCh.name}.
            Rename / delete / reorder / logo / poster affect the parent — edit those on the parent channel instead.
          </div>
          {!showRenaming ? (
            <button
              type="button"
              onClick={startShowRename}
              disabled={busy}
              data-testid="rename-show-button"
              title={`Bulk-rename every tape currently tagged "${currentCh.show_title || currentCh.name}" — this is what a sub-channel actually is.`}
              className="bg-[#FFAA33] text-black px-2 py-0.5 text-[10px] tracking-widest font-bold hover:opacity-80 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ✏ RENAME SHOW
            </button>
          ) : (
            <div
              className="flex flex-wrap items-center gap-2 mt-1"
              data-testid="rename-show-panel"
            >
              <span className="opacity-90 shrink-0">NEW SHOW NAME →</span>
              <input
                type="text"
                value={showRenameValue}
                onChange={(e) => setShowRenameValue(e.target.value.slice(0, 120))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleRenameShow();
                  else if (e.key === "Escape") cancelShowRename();
                }}
                autoFocus
                maxLength={120}
                data-testid="rename-show-input"
                className="flex-1 min-w-[160px] bg-black border border-[#FFAA33]/70 text-[var(--phosphor)] px-2 py-1 text-[11px] outline-none font-mono"
              />
              <button
                type="button"
                onClick={handleRenameShow}
                disabled={busy || !showRenameValue.trim() || showRenameValue.trim() === (currentCh.show_title || "").trim()}
                data-testid="rename-show-confirm"
                className="bg-[#FFAA33] text-black px-3 py-1 text-[10px] tracking-widest font-bold hover:opacity-80 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              >
                SAVE
              </button>
              <button
                type="button"
                onClick={cancelShowRename}
                disabled={busy}
                data-testid="rename-show-cancel"
                className="border border-neutral-600 text-neutral-400 px-2 py-1 text-[10px] tracking-widest hover:border-neutral-400 transition-colors disabled:opacity-40"
              >
                CANCEL
              </button>
              <span className="text-[9px] opacity-70 basis-full leading-snug normal-case tracking-normal">
                Renames every tape tagged &ldquo;{currentCh.show_title || currentCh.name}&rdquo; on CH {String(parentCh.number).padStart(2, "0")}. Curated show art transfers to the new name.
              </span>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 mt-2">
        <button
          onClick={() => handleReorder(-1)}
          disabled={busy || !selectedChannel || isSubChannel || idx <= 0}
          data-testid="reorder-up"
          title={isSubChannel ? "Sub-channels inherit their position from the parent" : "Move up"}
          className="px-2 border border-[var(--phosphor)] text-[11px] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          ▲
        </button>
        <button
          onClick={() => handleReorder(1)}
          disabled={busy || !selectedChannel || isSubChannel || idx < 0 || idx >= positionChannels.length - 1}
          data-testid="reorder-down"
          title={isSubChannel ? "Sub-channels inherit their position from the parent" : "Move down"}
          className="px-2 border border-[var(--phosphor)] text-[11px] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          ▼
        </button>
        <button
          onClick={startRename}
          disabled={busy || !selectedChannel || isSubChannel || renaming}
          data-testid="rename-channel-button"
          title={isSubChannel ? "Rename the parent channel instead" : "Rename channel"}
          className="flex-1 border border-[var(--phosphor)] py-1 text-[11px] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          RENAME
        </button>
        <button
          onClick={() => { setDeleteConfirm(true); setRenaming(false); }}
          disabled={busy || !selectedChannel || isSubChannel || deleteConfirm}
          data-testid="delete-channel-button"
          title={isSubChannel ? "Delete the parent channel from the parent view" : "Delete channel"}
          className="flex-1 border border-[#FF3B30] text-[#FF3B30] py-1 text-[11px] hover:bg-[#FF3B30] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          DELETE CH
        </button>
      </div>

      {/* Seasonal DORMANT / LIVE toggle. Preload tapes into a channel
          (Christmas, Halloween, Bake Off finale night…) while it stays
          hidden from viewers, then flip once to broadcast. No dates —
          the operator decides the moment. Hidden on subs (subs inherit
          their parent's visibility) and on virtual channels. Amber
          when dormant to catch the eye; phosphor when live. */}
      {selectedChannel && !isSubChannel && currentCh && !currentCh.virtual && (
        <button
          type="button"
          onClick={handleToggleDormant}
          disabled={busy || dormantBusy}
          data-testid="toggle-dormant-button"
          title={
            currentCh.is_dormant
              ? "Flip this channel LIVE — viewers will start seeing it in the guide and can tune in."
              : "Preload mode: hide this channel from viewers. It stays in your management panel but drops from every viewer surface."
          }
          className={`mt-2 w-full py-1.5 text-[11px] tracking-widest font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            currentCh.is_dormant
              ? "border border-[#FFAA33] text-[#FFAA33] hover:bg-[#FFAA33] hover:text-black"
              : "border border-[var(--phosphor)] text-[var(--phosphor)] hover:bg-[var(--phosphor)] hover:text-black"
          }`}
        >
          {currentCh.is_dormant
            ? "🌙 DORMANT — CLICK TO GO LIVE"
            : "📡 LIVE — CLICK TO GO DORMANT"}
        </button>
      )}

      {/* Jump-to-channel-number — types a target position and presses
          GO/Enter to splice the selected channel straight to that slot.
          Saves a ton of ▲/▼ clicks when reorganising a deep lineup.
          Hidden on sub-channels: their position derives from the parent. */}
      {selectedChannel && !isSubChannel && positionChannels.length > 1 && (
        <div className="mt-2 flex items-center gap-2" data-testid="channel-jump-row">
          <div className="text-[10px] opacity-80 shrink-0">&gt; MOVE TO CH</div>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={positionChannels.length}
            step={1}
            value={jumpTarget}
            onChange={(e) => setJumpTarget(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleJumpTo();
              }
            }}
            placeholder={String(currentCh?.number || 1).padStart(2, "0")}
            data-testid="channel-jump-input"
            className="w-16 bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-1 text-[11px] outline-none text-center tabular-nums placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)]"
          />
          <div className="text-[10px] opacity-50 tabular-nums shrink-0">/ {String(positionChannels.length).padStart(2, "0")}</div>
          <button
            onClick={handleJumpTo}
            disabled={busy || !jumpTarget}
            data-testid="channel-jump-go"
            className="flex-1 border border-[var(--phosphor)] py-1 text-[11px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            &gt;&gt; GO
          </button>
        </div>
      )}

      {renaming && (
        <div className="mt-2 border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] p-2" data-testid="rename-panel">
          <div className="text-[10px] opacity-80 mb-1">&gt; CHANNEL NAME:</div>
          <input
            type="text"
            autoFocus
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setRenaming(false);
            }}
            data-testid="rename-input"
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-1.5 text-xs outline-none mb-2"
          />
          <div className="text-[10px] opacity-80 mb-1">
            &gt; DESCRIPTION (optional — shown in the guide):
          </div>
          <textarea
            value={descValue}
            onChange={(e) => setDescValue(e.target.value.slice(0, 300))}
            placeholder="e.g. 24/7 vintage MTV music videos and concert footage"
            rows={2}
            maxLength={300}
            data-testid="channel-description-input"
            className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-1.5 text-[11px] outline-none resize-none placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)]"
          />
          <div className="text-[9px] opacity-60 mb-2 text-right tabular-nums">
            {descValue.length}/300
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleRename}
              disabled={busy}
              data-testid="rename-confirm"
              className="flex-1 border border-[var(--phosphor)] py-1.5 text-[11px] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40"
            >
              SAVE
            </button>
            <button
              onClick={() => setRenaming(false)}
              data-testid="rename-cancel"
              className="border border-neutral-600 text-neutral-400 px-3 text-[11px] hover:border-neutral-400 transition-colors"
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="mt-2 border border-[#FF3B30]/70 p-2" data-testid="delete-confirm-panel">
          <div className="text-[10px] text-[#FF3B30] mb-2">
            &gt; DELETE THIS CHANNEL
            {channelVideoCount > 0 && (
              <> AND ALL <span className="font-bold">{channelVideoCount}</span> TAPE{channelVideoCount === 1 ? "" : "S"}</>
            )}
            ? IRREVERSIBLE.
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleDelete}
              disabled={busy}
              data-testid="delete-confirm"
              className="flex-1 border border-[#FF3B30] text-[#FF3B30] py-1 text-[11px] hover:bg-[#FF3B30] hover:text-black transition-colors disabled:opacity-40"
            >
              YES — DESTROY
            </button>
            <button
              onClick={() => setDeleteConfirm(false)}
              data-testid="delete-cancel"
              className="flex-1 border border-neutral-600 text-neutral-400 py-1 text-[11px] hover:border-neutral-400 transition-colors"
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {selectedChannel && !isSubChannel && !currentCh?.virtual && (
        <div className="mt-3 border border-[var(--phosphor)] p-2 bg-[color-mix(in_srgb,var(--phosphor)_5%,transparent)]">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.18em]">&gt; TMDB CHANNEL ARTWORK</div>
              <div className="text-[9px] opacity-60 mt-0.5">
                Search once · verify the exact title · set thumbnail, station logo and guide poster together.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setTmdbArtworkOpen(true)}
              disabled={busy}
              data-testid="open-tmdb-channel-artwork"
              className="shrink-0 text-[10px] tracking-widest border border-[var(--phosphor)] px-3 py-1.5 hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40"
            >
              🎬 TMDB ARTWORK
            </button>
          </div>
        </div>
      )}

      {selectedChannel && (
        // For sub-channels: the thumbnail path routes to the parent's
        // per-show art (via linkAsset → /channels/{parent}/show-art/link).
        // The <img> preview here shows the parent's channel thumbnail
        // as a fallback since the sub's synthetic id has no thumbnail
        // endpoint. On sub-channels only LINK URL is supported — the
        // AssetPanel switches to link-only mode below.
        <AssetPanel
          testId="channel-thumb-panel"
          label={isSubChannel ? `SUB-CHANNEL THUMBNAIL (SHOW ART for "${currentCh?.show_title || currentCh?.name}")` : "CHANNEL THUMBNAIL"}
          inputRef={thumbInputRef}
          inputTestId="channel-thumb-input"
          clearTestId="clear-channel-thumb"
          accept="image/*"
          src={isSubChannel
            ? (currentCh?.thumbnail_url || null)
            : `${apiBase}/channels/${editChannelId}/thumbnail?v=${thumbBust}`}
          hasManual={isSubChannel
            ? !!(parentCh?.show_art && parentCh.show_art[currentCh.show_title])
            : !!(currentCh?.thumbnail_path || currentCh?.thumbnail_url)}
          currentUrl={isSubChannel
            ? ((parentCh?.show_art && parentCh.show_art[currentCh.show_title]) || "")
            : (currentCh?.thumbnail_url || "")}
          manualHint={isSubChannel ? "Per-show art override active on the parent." : "Manual override active"}
          autoHint={isSubChannel ? "Falls back to first tape's TMDb backdrop." : "Auto: first frame of first tape"}
          objectFit="cover"
          linkOnly={isSubChannel}
          onUpload={(f) => uploadAsset("thumbnail", f)}
          urlDraft={artworkDrafts.thumbnail}
          onUrlChange={(u) => setArtworkDraft("thumbnail", u)}
          onClear={() => clearAsset("thumbnail")}
          onTmdb={isSubChannel ? null : () => setTmdbTargetKind("thumbnail")}
          busy={busy}
        />
      )}

      {selectedChannel && !isSubChannel && (
        <AssetPanel
          testId="channel-logo-panel"
          label="STATION LOGO (TRANSPARENT PNG)"
          inputRef={logoInputRef}
          inputTestId="channel-logo-input"
          clearTestId="clear-channel-logo"
          accept="image/png,image/svg+xml,image/webp,image/jpeg"
          src={(currentCh?.logo_path || currentCh?.logo_url) ? `${apiBase}/channels/${editChannelId}/logo?v=${thumbBust}` : null}
          hasManual={!!(currentCh?.logo_path || currentCh?.logo_url)}
          currentUrl={currentCh?.logo_url || ""}
          manualHint="On air. Watermarked when this channel is tuned."
          autoHint="Displayed as a small watermark on the TV when this channel is tuned."
          objectFit="contain"
          clearLabel="REMOVE LOGO"
          onUpload={(f) => uploadAsset("logo", f)}
          urlDraft={artworkDrafts.logo}
          onUrlChange={(u) => setArtworkDraft("logo", u)}
          onClear={() => clearAsset("logo")}
          busy={busy}
        />
      )}

      {selectedChannel && !isSubChannel && (
        // GUIDE POSTER — one image/GIF used as the channel-guide mini
        // preview tile for every MEGA (or otherwise poster-less) tape
        // on this channel. Kept alongside logo + thumbnail so the
        // operator manages all channel artwork in one place. Not
        // available on sub-channels — their guide poster derives from
        // per-show art or the first tape's backdrop.
        <AssetPanel
          testId="channel-guide-poster-panel"
          label="GUIDE POSTER (JPG / PNG / WEBP / GIF)"
          inputRef={guidePosterInputRef}
          inputTestId="channel-guide-poster-input"
          clearTestId="clear-channel-guide-poster"
          accept="image/jpeg,image/png,image/webp,image/gif"
          src={(currentCh?.guide_poster_path || currentCh?.guide_poster_url) ? `${apiBase}/channels/${editChannelId}/guide_poster?v=${thumbBust}` : null}
          hasManual={!!(currentCh?.guide_poster_path || currentCh?.guide_poster_url)}
          currentUrl={currentCh?.guide_poster_url || ""}
          manualHint="Live on the channel guide as the preview tile for MEGA tapes (and any tape without its own poster)."
          autoHint="Upload a 4:3 image or animated GIF (max 8 MB) to give every MEGA tape on this channel a real preview tile."
          objectFit="cover"
          clearLabel="REMOVE POSTER"
          onUpload={(f) => uploadAsset("guide_poster", f)}
          urlDraft={artworkDrafts.guide_poster}
          onUrlChange={(u) => setArtworkDraft("guide_poster", u)}
          onClear={() => clearAsset("guide_poster")}
          onTmdb={() => setTmdbTargetKind("guide_poster")}
          busy={busy}
        />
      )}

      {selectedChannel && (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={saveArtworkUrls}
            disabled={busy || !artworkDirty}
            data-testid="save-channel-artwork"
            className="text-[10px] tracking-widest border border-[var(--phosphor)] px-4 py-1.5 hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            SAVE ARTWORK
          </button>
        </div>
      )}

      <TmdbChannelArtworkModal
        open={tmdbArtworkOpen && !!selectedChannel && !currentCh?.virtual && !isSubChannel}
        initialQuery={currentCh?.name || ""}
        targetId={editChannelId}
        apiBase={apiBase}
        authHeaders={authHeaders}
        onClose={() => setTmdbArtworkOpen(false)}
        onApplied={async () => {
          setThumbBust(Date.now());
          try {
            const raw = localStorage.getItem("crt_thumb_cache_v1");
            if (raw) {
              const obj = JSON.parse(raw);
              const pref = `${apiBase}/channels/${editChannelId}/thumbnail`;
              for (const k of Object.keys(obj || {})) {
                if (k.startsWith(pref)) delete obj[k];
              }
              localStorage.setItem("crt_thumb_cache_v1", JSON.stringify(obj));
            }
          } catch { /* private-mode / quota — skip */ }
          if (onChannelsChanged) await onChannelsChanged();
        }}
      />

      {/* Legacy per-slot picker retained during Phase 2.3.1 regression testing. */}
      {/* TMDb picker for channel-level artwork. Both the CHANNEL
          THUMBNAIL and GUIDE POSTER buttons feed into the same modal;
          the target kind selects which field the applied poster URL
          lands in. TMDb response body already contains a fresh channel
          doc which we push into `onChannelsChanged` so the CRT +
          guide + editor picture all refresh in lock-step. */}
      <TmdbLookupModal
        open={!!tmdbTargetKind && !!selectedChannel && !currentCh?.virtual && !isSubChannel}
        initialQuery={currentCh?.name || ""}
        mode="channel"
        targetId={editChannelId}
        apiBase={apiBase}
        authHeaders={authHeaders}
        onClose={() => setTmdbTargetKind(null)}
        onApplied={async () => {
          setThumbBust(Date.now());
          // Clear the localStorage thumb cache for the channel's
          // thumbnail endpoint so the guide/CRT/editor image swap to
          // the freshly-linked TMDb URL on the next render instead of
          // serving the previous URL's bytes from cache.
          try {
            const raw = localStorage.getItem("crt_thumb_cache_v1");
            if (raw) {
              const obj = JSON.parse(raw);
              const pref = `${apiBase}/channels/${editChannelId}/thumbnail`;
              for (const k of Object.keys(obj || {})) {
                if (k.startsWith(pref)) delete obj[k];
              }
              localStorage.setItem("crt_thumb_cache_v1", JSON.stringify(obj));
            }
          } catch { /* private-mode / quota — skip */ }
          if (onChannelsChanged) await onChannelsChanged();
        }}
      />
    </div>
  );
}

function AssetPanel({
  testId, label, inputRef, inputTestId, clearTestId, accept,
  src, hasManual, currentUrl, urlDraft, onUrlChange, manualHint, autoHint, objectFit, clearLabel,
  onUpload, onClear, onTmdb, busy, linkOnly = false,
}) {
  return (
    <div className="mt-3 border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] p-2" data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <div className="opacity-80 text-[10px]">&gt; {label}:</div>
        <div className="flex gap-1">
          {!linkOnly && (
            <>
              <input
                ref={inputRef}
                type="file"
                accept={accept}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onUpload(file);
                  e.target.value = "";
                }}
                data-testid={inputTestId}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => inputRef?.current?.click()}
                disabled={busy}
                data-testid={`${testId}-upload`}
                className="text-[9px] px-1.5 py-0.5 border tracking-widest border-[color-mix(in_srgb,var(--phosphor)_50%,transparent)] hover:bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)] disabled:opacity-40"
              >
                UPLOAD
              </button>
            </>
          )}
          {onTmdb && (
            <button
              type="button"
              onClick={onTmdb}
              data-testid={`${testId}-mode-tmdb`}
              title="Pull a poster from TheMovieDB (writes both poster URL + synopsis)"
              className="text-[9px] px-1.5 py-0.5 border tracking-widest border-[color-mix(in_srgb,var(--phosphor)_50%,transparent)] hover:bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)]"
            >
              🎬 TMDB
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {src ? (
          <img
            src={src}
            alt=""
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            onLoad={(e) => { e.currentTarget.style.display = "block"; }}
            className={`w-20 h-14 ${objectFit === "contain" ? "object-contain p-1" : "object-cover"} border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] bg-black`}
          />
        ) : (
          <div className="w-20 h-14 border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] flex items-center justify-center text-[8px] text-[color-mix(in_srgb,var(--phosphor)_50%,transparent)] bg-black">
            NONE
          </div>
        )}
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <input
            type="url"
            value={urlDraft || ""}
            onChange={(e) => onUrlChange(e.target.value)}
            placeholder="Paste direct artwork URL"
            data-testid={`${testId}-url-input`}
            className="w-full min-w-0 bg-black border border-[var(--phosphor)] text-[var(--phosphor)] px-1.5 py-0.5 text-[10px] outline-none placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)]"
          />
          {hasManual && (
            <button
              onClick={onClear}
              disabled={busy}
              data-testid={clearTestId}
              className="text-[10px] border border-neutral-600 text-neutral-400 py-0.5 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
            >
              {clearLabel || "CLEAR (use auto)"}
            </button>
          )}
          <div className="text-[9px] opacity-60 leading-snug">
            {currentUrl
              ? "Direct image URL active. Edit any artwork URL above, then use SAVE ARTWORK once for the channel."
              : (hasManual ? manualHint : autoHint)}
          </div>
        </div>
      </div>
    </div>
  );
}

